import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  DEFAULT_ACCENT,
  INTRO_DIP_MS,
  INTRO_LIFT_MS,
  INTRO_MAX_MS,
  INTRO_MIN_MS,
  INTRO_STILL_MS,
  PROVIDER_EYEBROW,
  PROVIDER_NAME,
  WORDMARK_SRC,
} from "./identity";
import { INTRO_DONE_EVENT } from "./introGate";
import type { SplashHandle } from "./splash";
import ProviderBadge from "./ProviderBadge";
import "./provider.css";

type Props = {
  /** The game underneath, for the accent stylesheet and the badge. */
  gameId: string;
  /** The game's accent — the whole sequence is tinted with it. */
  accent?: string;
  /** The game's display name, shown under the loading bar. */
  title: string;
  children: ReactNode;
};

/**
 * The provider title sequence, wrapped around a game.
 *
 * THE GAME BOOTS UNDERNEATH IT. `children` renders immediately and normally;
 * the sequence is a fixed overlay on top. That is the whole point — the four or
 * so seconds the mark is on screen are seconds the game spends compiling
 * shaders, decoding audio and laying out its canvas, so the splash costs almost
 * nothing in real time. An overlay that gated `children` behind it would turn
 * those seconds into pure delay and then start the load.
 *
 * HOW LONG IT HOLDS. Until the real `load` event, floored at `INTRO_MIN_MS`,
 * ceilinged at `INTRO_MAX_MS`, and gated on the animation having actually
 * finished drawing the mark. That last condition is not redundant with the
 * floor: the floor is wall-clock and the animation is not, so on a device slow
 * enough to drop frames the timer comes due while the signature is still being
 * written (see `settled()` in splash.ts). A game that is not ready by the
 * ceiling gets cut off regardless, because at that point the player is waiting
 * rather than watching — and the 3D holds the finished mark for that whole wait
 * rather than dispersing into an empty frame. Tapping skips immediately.
 *
 * WHEN THERE IS NO WEBGL — a locked-down browser, a lost context, or a player
 * who has asked for reduced motion — it falls back to the mark held still under
 * a soft glow, for well under half the time. The fallback is not an error state;
 * it is the same screen with the motion taken out of it.
 */
export default function ProviderIntro({ gameId, accent, title, children }: Props) {
  // "hold" → the sequence owns the screen. "exit" → cross-fading to the game.
  // "gone" → unmounted; nothing of this component is on screen or in memory.
  // "hold" → the sequence owns the screen. "dip" → going to black.
  // "lift" → the black coming off the game. "gone" → unmounted.
  const [phase, setPhase] = useState<"hold" | "dip" | "lift" | "gone">("hold");
  // Set when WebGL is unavailable or the player asked for reduced motion.
  const [still, setStill] = useState(false);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const splashRef = useRef<SplashHandle | null>(null);
  const leaveRef = useRef<() => void>(() => {});

  useEffect(() => {
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    /**
     * Leave. Idempotent: the max timer, the load event and a player tap all race
     * to call it and only the first one counts.
     */
    let leaving = false;
    const leave = (forced = false) => {
      if (leaving || cancelled) return;
      // Hold for a mark that is still being drawn. The floor below is wall-clock
      // and the animation is not (see `settled()` in splash.ts), so on a slow
      // device the timer can come due while the signature is half written — the
      // exact thing this screen exists to show. The ceiling passes `forced`, so
      // this can only ever delay the exit as far as INTRO_MAX_MS.
      if (!forced && splashRef.current && !splashRef.current.settled()) {
        timers.push(setTimeout(() => leave(), 120));
        return;
      }
      leaving = true;
      // Release the swarm. It has been holding the finished mark until now, so
      // the dispersal reads under the cross-fade rather than being a shot the
      // player never gets to see.
      splashRef.current?.finish();
      setPhase("dip");
      timers.push(
        setTimeout(() => {
          if (cancelled) return;
          setPhase("lift");
          // The black is coming off and the game's own screen is what appears
          // next — this is the moment anything that should greet the player
          // rather than the loading screen has been waiting for. The music beds
          // hang off it; see `introGate.ts`.
          window.dispatchEvent(new Event(INTRO_DONE_EVENT));
        }, INTRO_DIP_MS)
      );
      timers.push(
        setTimeout(() => !cancelled && setPhase("gone"), INTRO_DIP_MS + INTRO_LIFT_MS)
      );
    };
    // A tap is an explicit request to skip, so it forces past the settled gate.
    leaveRef.current = () => leave(true);
    const onLoad = () => leave();

    if (reduced) {
      setStill(true);
      timers.push(setTimeout(() => leave(true), INTRO_STILL_MS));
      return () => {
        cancelled = true;
        timers.forEach(clearTimeout);
      };
    }

    // three, the composer and the bloom pass are ~200KB that no page other than
    // this one needs, so they are fetched at mount rather than bundled into
    // every route that renders a game.
    import("./splash")
      .then(({ createProviderSplash }) => {
        if (cancelled || !hostRef.current) return null;
        return createProviderSplash(hostRef.current, { accent });
      })
      .then((handle) => {
        if (!handle) return;
        if (cancelled) {
          handle.dispose();
          return;
        }
        splashRef.current = handle;
      })
      .catch(() => {
        // No WebGL, no half-float, a blocked chunk — all the same outcome, and
        // all of them still get a title screen.
        if (!cancelled) setStill(true);
      });

    // The floor, then the real load event, then the ceiling.
    timers.push(
      setTimeout(() => {
        if (document.readyState === "complete") leave();
        else window.addEventListener("load", onLoad, { once: true });
      }, INTRO_MIN_MS)
    );
    // The ceiling forces it: past here the player is waiting, not watching.
    timers.push(setTimeout(() => leave(true), INTRO_MAX_MS));

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      window.removeEventListener("load", onLoad);
      splashRef.current?.dispose();
      splashRef.current = null;
    };
  }, [accent]);

  // Free the GPU the moment the overlay is off screen, rather than waiting for
  // the player to navigate away — the game underneath wants that memory now.
  useEffect(() => {
    if (phase !== "lift" && phase !== "gone") return;
    splashRef.current?.dispose();
    splashRef.current = null;
  }, [phase]);

  return (
    <>
      {children}
      <ProviderBadge gameId={gameId} accent={accent} />
      {phase !== "gone" && (
        <>
          {/* Start the artwork downloading NOW, not when the WebGL chunk lands.
              Without this the two are strictly sequential — three.js parses,
              then `rasterizeWordmark` asks for a 132KB SVG that nothing has
              requested yet — and that round trip is dead time the player spends
              looking at an empty backdrop. This app is client-rendered, so the
              tag lands in the body rather than the head (React 18 does not hoist
              it), which browsers honour for `preload` all the same: what matters
              is that the request is in flight before the dynamic import resolves. */}
          <link rel="preload" as="image" href={WORDMARK_SRC} type="image/svg+xml" />
          <style>{`.provider-intro[data-game="${gameId}"]{--provider-accent:${
            accent ?? DEFAULT_ACCENT
          }}`}</style>
          <div
            className={`provider-intro${phase === "dip" ? " is-dipping" : ""}${
              phase === "lift" ? " is-lifting" : ""
            }${still ? " is-still" : ""}`}
            data-game={gameId}
            role="status"
            aria-live="polite"
            aria-label={`${PROVIDER_NAME} — loading ${title}`}
            onPointerDown={() => leaveRef.current()}
          >
            <div className="provider-intro__stage" ref={hostRef} aria-hidden="true" />

            {/* The still fallback, hidden by CSS unless `is-still` is set. It is
                in the markup unconditionally so that the switch to it costs a
                class change rather than an image request — by the time we know
                WebGL has failed, waiting on a network round trip to show the
                logo would defeat the point of having a fallback. */}
            <img
              className="provider-intro__still"
              src={WORDMARK_SRC}
              alt=""
              aria-hidden="true"
            />

            <div className="provider-intro__chrome">
              <p className="provider-intro__eyebrow">{PROVIDER_EYEBROW}</p>
              <div className="provider-intro__bar">
                <span className="provider-intro__fill" />
              </div>
              <p className="provider-intro__title">Loading {title}</p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
